/**
 * IMPRESSUMPAGE.JS - Impressum (Legal Notice)
 *
 * Pflichtangaben nach § 5 TMG
 * Weißes Design + Planivio Teal/Green (gleich wie DatenschutzPage)
 *
 * @version 1.0.0
 */

import React, { useState } from 'react';
import { useTranslation, LANGUAGES } from '../context/I18nContext';
import { usePublicPageView } from '../hooks/useAnalytics';

const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)'
};

const COLORS = {
  bgWhite: '#FFFFFF',
  bgLight: '#F8FAFC',
  textDark: '#1E293B',
  textMedium: '#475569',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  accent: PLANIVIO.teal
};

const sectionStyle = {
  marginBottom: '40px',
  paddingBottom: '30px',
  borderBottom: `1px solid ${COLORS.border}`
};

const headingStyle = {
  fontSize: '1.4rem',
  fontWeight: 700,
  marginBottom: '20px',
  color: COLORS.textDark
};

const textStyle = {
  color: COLORS.textMedium,
  fontSize: '1rem',
  lineHeight: 1.8,
  marginBottom: '15px'
};

const linkStyle = {
  color: COLORS.accent,
  textDecoration: 'none',
  cursor: 'pointer'
};

function ImpressumPage({ onNavigate }) {
  usePublicPageView('impressum');
  const { t, language, setLanguage, currentLanguage } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  return (
    <div style={{ background: COLORS.bgWhite, minHeight: '100vh', color: COLORS.textDark }}>

      {/* Navigation */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        background: COLORS.bgWhite,
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        borderBottom: `1px solid ${COLORS.border}`,
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
      }}>
        <div
          onClick={() => onNavigate && onNavigate('landing')}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
        >
          <img
            src="/images/Logos/planivio_logo_primary_400px.png"
            alt="Planivio"
            style={{ height: '36px', width: 'auto' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.accent }}>planivio</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            style={{
              padding: '8px 14px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.textMedium,
              cursor: 'pointer',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <span>{currentLanguage.flag}</span>
            <span>{currentLanguage.code.toUpperCase()}</span>
            <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>&#9660;</span>
          </button>

          <button
            onClick={() => onNavigate && onNavigate('landing')}
            style={{
              padding: '10px 24px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              color: COLORS.textMedium,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            &larr; {t('common.back')}
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 40px' }}>

        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 800,
          marginBottom: '20px',
          background: PLANIVIO.gradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          {t('legal.imprint.title')}
        </h1>

        <p style={{ color: COLORS.textMuted, marginBottom: '40px', fontSize: '1rem' }}>
          Angaben gem&auml;&szlig; &sect; 5 TMG
        </p>

        {/* Anbieter */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>Diensteanbieter</h2>
          <div style={{
            background: COLORS.bgLight,
            padding: '20px',
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`
          }}>
            <p style={{ ...textStyle, marginBottom: '5px' }}><strong>Brice Fotzo</strong></p>
            <p style={{ ...textStyle, marginBottom: '5px' }}>Johannisstr. 105</p>
            <p style={{ ...textStyle, marginBottom: '5px' }}>90419 N&uuml;rnberg</p>
            <p style={textStyle}>{t('legal.imprint.germany')}</p>
          </div>
        </section>

        {/* Kontakt */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>Kontakt</h2>
          <p style={textStyle}>
            {t('legal.imprint.email')}:{' '}
            <a href="mailto:kontakt@planivio.de" style={linkStyle}>kontakt@planivio.de</a>
          </p>
          <p style={textStyle}>
            Website:{' '}
            <a href="https://planivio.de" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              https://planivio.de
            </a>
          </p>
        </section>

        {/* Verantwortlich für den Inhalt */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>Verantwortlich f&uuml;r den Inhalt nach &sect; 18 Abs. 2 MStV</h2>
          <p style={textStyle}>
            Brice Fotzo<br />
            Johannisstr. 105<br />
            90419 N&uuml;rnberg
          </p>
        </section>

        {/* EU-Streitschlichtung */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>EU-Streitschlichtung</h2>
          <p style={textStyle}>
            Die Europ&auml;ische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://ec.europa.eu/consumers/odr
            </a>
          </p>
          <p style={textStyle}>
            Unsere E-Mail-Adresse finden Sie oben im Impressum.
          </p>
        </section>

        {/* Verbraucherstreitbeilegung */}
        <section style={{ ...sectionStyle, borderBottom: 'none' }}>
          <h2 style={headingStyle}>Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
          <p style={textStyle}>
            Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
            Verbraucherschlichtungsstelle teilzunehmen.
          </p>

          <p style={{ color: COLORS.textMuted, fontSize: '0.9rem', marginTop: '30px' }}>
            {t('legal.imprint.lastUpdated')}: Februar 2026
          </p>
        </section>

      </div>

      {/* Footer */}
      <footer style={{
        padding: '30px 40px',
        borderTop: `1px solid ${COLORS.border}`,
        background: COLORS.bgLight,
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', gap: '25px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span
            onClick={() => onNavigate && onNavigate('datenschutz')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.privacy')}
          </span>
          <span
            onClick={() => onNavigate && onNavigate('agb')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.terms')}
          </span>
          <span style={{ color: COLORS.accent, fontSize: '0.9rem', fontWeight: 600 }}>
            {t('landing.footer.imprint')}
          </span>
        </div>
        <div style={{ marginTop: '20px', color: COLORS.textMuted, fontSize: '0.8rem' }}>
          &copy; 2026 Planivio. {t('landing.footer.rights')}
        </div>
      </footer>

      {/* Language Dropdown */}
      {showLanguageMenu && (
        <>
          <div
            onClick={() => setShowLanguageMenu(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
          />
          <div style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            background: COLORS.bgWhite,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '8px',
            minWidth: '160px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 99999
          }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setShowLanguageMenu(false); }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: language === lang.code ? PLANIVIO.gradient : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: language === lang.code ? 'white' : COLORS.textDark,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                  marginBottom: '4px'
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>{lang.flag}</span>
                <span>{lang.name}</span>
                {language === lang.code && <span style={{ marginLeft: 'auto', opacity: 0.8 }}>&#10003;</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ImpressumPage;
